import React from 'react'
import {updateMember} from '../../api/action/updateMember'
import {useAppDispatch} from '../index'

const writingIndicatorTimeoutInMs = 30 * 1000 // 30 second
type writingIndicator = () => void
export type WritingIndicatorHookProps = {
    channel_id?: string
    idle?: number
    user_id: string
}

const useWritingIndicator = ({
    channel_id, user_id, idle,
}: WritingIndicatorHookProps): [writingIndicator, writingIndicator] => {
    const dispatch = useAppDispatch()

    const [channelId, setChannelId] = React.useState <WritingIndicatorHookProps['channel_id']>()
    const [timeoutId, setTimeoutId] = React.useState<ReturnType<typeof setTimeout>|null>()
    const [isWriting, setIsWriting] = React.useState<boolean>(false)

    const handleUpdates = (writing: boolean): void => {
        // if previous state is same as coming state then ignore
        if (isWriting === writing) {
            return
        }
        // update state and sync with server using dispatch event
        setIsWriting(writing)
        dispatch(updateMember({
            channel_id: channelId,
            member_attrs: {writing},
            user_id,
        }))
    }

    const resetTimeout = () => {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        setTimeoutId(null)
    }

    const stopWriting = () => {
        resetTimeout()
        handleUpdates(false)
    }

    const startWriting = () => {
        resetTimeout()
        // start timer event that will dispatch stop writing if nothing happens within `writingIndicatorTimeoutInMs`
        setTimeoutId(setTimeout(stopWriting, writingIndicatorTimeoutInMs))
        handleUpdates(true)
    }

    React.useEffect(() => {
        // 1.first we can sync with server using dispatch event with previously writing channel_id
        stopWriting()
        // 2.now update the channel_id
        setChannelId(channel_id)
    }, [channel_id])

    React.useEffect(() => {
        stopWriting()
    }, [user_id])

    React.useEffect(() => {
        if (idle) {
            stopWriting()
        }
    }, [idle])

    return [startWriting, stopWriting]
}

export {useWritingIndicator}
